import { cn } from '@/lib/utils';
import { useAppTheme } from '@/providers/theme-provider';
import * as React from 'react';
import { Animated, Pressable, View } from 'react-native';

const TRACK_WIDTH = 50;
const TRACK_HEIGHT = 28;
const THUMB_SIZE = 24;
const TRACK_PADDING = 2;
const THUMB_TRAVEL = TRACK_WIDTH - THUMB_SIZE - TRACK_PADDING * 2;

interface SwitchProps {
  value?: boolean;
  onValueChange?: (value: boolean) => void;
  disabled?: boolean;
  className?: string;
  /** Names what the switch controls for screen readers (the row's label). */
  accessibilityLabel?: string;
}

const Switch = React.forwardRef<View, SwitchProps>(
  ({ className, value = false, onValueChange, disabled = false, accessibilityLabel }, ref) => {
    const theme = useAppTheme();
    const progress = React.useRef(new Animated.Value(value ? 1 : 0)).current;

    React.useEffect(() => {
      Animated.timing(progress, {
        toValue: value ? 1 : 0,
        duration: 250,
        useNativeDriver: false,
      }).start();
    }, [value, progress]);

    const trackColor = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [theme.colors.border, theme.colors.primary],
    });

    const thumbTranslateX = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, THUMB_TRAVEL],
    });

    const thumbColor = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [theme.colors.text, theme.colors.background],
    });

    return (
      <View ref={ref} className={cn('items-center justify-center', className)}>
        <Pressable
          onPress={() => onValueChange?.(!value)}
          disabled={disabled}
          accessibilityRole="switch"
          accessibilityLabel={accessibilityLabel}
          accessibilityState={{ checked: value, disabled }}
          style={{ opacity: disabled ? 0.4 : 1 }}
        >
          <Animated.View
            style={{
              width: TRACK_WIDTH,
              height: TRACK_HEIGHT,
              borderRadius: TRACK_HEIGHT / 2,
              padding: TRACK_PADDING,
              justifyContent: 'center' as const,
              backgroundColor: trackColor,
            }}
          >
            <Animated.View
              style={{
                width: THUMB_SIZE,
                height: THUMB_SIZE,
                borderRadius: THUMB_SIZE / 2,
                backgroundColor: thumbColor,
                transform: [{ translateX: thumbTranslateX }],
              }}
            />
          </Animated.View>
        </Pressable>
      </View>
    );
  },
);

Switch.displayName = 'Switch';

export { Switch };
export type { SwitchProps };
